// One conversation entry (spec §3.8): the user's question, or a reply with its text, rules lines (i18n by code),
// action cards / result lines, suggestion chips (last reply only) and the mode, budget and cost notes.
// R29: an action card's title/button is fixed i18n copy from its type + args + trusted subject; the model's own
// label/reason is secondary text only, and hidden when the reply read untrusted content (tainted).
import { Markdown } from "../../components/Markdown.tsx";
import { useEffect, useState, type ReactNode } from "react";
import { en, type MessageKey } from "../../i18n/en.ts";
import { useI18n, type Translate } from "../../i18n/index.tsx";
import type { AssistantAction, AssistantMessage, AssistantReply, AssistantRulesLine } from "../../api/types-assistant.ts";
import { Button } from "../../components/Button.tsx";
import { CheckCircleIcon, LoaderIcon, XCircleIcon } from "../../components/icons.tsx";
import { formatCents } from "../../lib/format.ts";
import { projectIdOf, type ActionResult } from "./actions.ts";
import { ASSISTANT_MAX_FOLLOW_UPS } from "./operator-loop.ts";
import type { AssistantAttachmentRef, AssistantTurnMeta } from "@maestros/contracts";
import { AttachmentChips } from "../composer/AttachmentChips.tsx";
import { DeleteProjectFacts } from "./DeleteProjectFacts.tsx";
import { DecisionAction } from "./GrantApprovalCard.tsx";
import { formatTurnDuration, turnTiming } from "./timing.ts";
import { ConversationArtifact } from "./ConversationArtifact.tsx";

function MessageTiming({ message, answeredTurn }: { message: AssistantMessage; answeredTurn?: AssistantTurnMeta | undefined }) {
  const { t, locale } = useI18n();
  const timestamp = (value: string) => new Intl.DateTimeFormat(locale, { dateStyle: "short", timeStyle: "medium" }).format(new Date(value));
  const candidate = message.author_role === "user" ? message.turn : answeredTurn ?? message.turn;
  const turn = message.reply && candidate?.id !== message.reply.turn_id ? null : candidate;
  const [now, setNow] = useState(Date.now);
  const active = turn?.state === "running" && Number.isFinite(Date.parse(turn.started_at ?? "")) && !turn.completed_at;
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active, turn?.id]);
  const timing = turnTiming(turn, now);
  const validCreatedAt = Number.isFinite(Date.parse(message.created_at));
  return (
    <p className="assistant-note" data-testid="assistant-message-timing">
      {message.author_role === "assistant" ? <span>{message.reply?.agent_name || t("assistant.title")} · </span> : null}
      {validCreatedAt ? <time dateTime={message.created_at} title={timestamp(message.created_at)}>{timestamp(message.created_at)}</time> : null}
      {message.author_role === "assistant" && turn?.started_at && Number.isFinite(Date.parse(turn.started_at)) ? <span> · {t("assistant.timing.started")} <time dateTime={turn.started_at}>{timestamp(turn.started_at)}</time></span> : null}
      {timing ? <span data-testid="assistant-turn-duration"> · {t(timing.active ? "assistant.timing.elapsed" : "assistant.timing.duration", { duration: formatTurnDuration(timing.milliseconds) })}</span> : null}
    </p>
  );
}

/** D36: the chips of a turn's attachments (display facts only). */
function FileChips({ files, label, testId }: { files: readonly AssistantAttachmentRef[] | undefined; label?: string; testId: string }) {
  if (!files || files.length === 0) return null;
  return <AttachmentChips items={files.map((a) => ({ key: a.id, name: a.name, size: a.size_bytes, kind: a.kind, mime: a.mime, attachmentId: a.id }))} testId={testId} {...(label ? { label } : {})} />;
}

/** What this tab did with an action (the server ledger in `reply.claims` covers everything else). */
export type LocalOutcome = { state: "running" } | { state: "done"; result: ActionResult } | { state: "elsewhere" } | { state: "dismissed" };

function keyOrNull(key: string): MessageKey | null {
  return key in en ? (key as MessageKey) : null;
}

/** A rules line through `assistant.line.<code>`; `null` for a code this build does not know. */
function lineText(t: Translate, line: AssistantRulesLine): string | null {
  const key = keyOrNull(`assistant.line.${line.code}`);
  return key ? t(key, line.params) : null;
}

/** A result detail token (never server text): translated, or nothing for a token this build does not know. */
function detailText(t: Translate, detail: string | null): string {
  const key = detail ? keyOrNull(`assistant.detail.${detail}`) : null;
  return key ? ` — ${t(key)}` : "";
}

/** The page an `open_page` path names, through the app's own navigation labels. */
function pageName(t: Translate, path: string): string {
  if (path === "/") return t("nav.home");
  const project = /^\/projects\/[^/]+(?:\/([a-z]+))?$/.exec(path);
  if (project) return t(keyOrNull(`project.tab.${project[1] ?? "overview"}`) ?? "project.tab.overview");
  const settings = /^\/settings\/([a-z]+)$/.exec(path)?.[1];
  if (settings) {
    const tab = keyOrNull(`settings.tab.${settings}`);
    return tab ? `${t("nav.settings")} › ${t(tab)}` : t("nav.settings");
  }
  const top = keyOrNull(`nav.${path.slice(1)}`);
  return top ? t(top) : path;
}

/** The card's fixed title (also its button): type + args + the domain-set subject, never the model's label (R29). */
export function actionTitle(t: Translate, action: AssistantAction, sessionProjectId: string | null): string {
  const subject = action.subject ?? null;
  switch (action.type) {
    case "open_page": {
      const page = pageName(t, action.args.path);
      if (subject !== null) return t("assistant.actionTitle.open_page_of", { page, project: subject });
      const target = projectIdOf(action.args.path);
      return t(target !== null && target !== sessionProjectId ? "assistant.actionTitle.open_page_other" : "assistant.actionTitle.open_page", { page });
    }
    case "retry_task":
    case "unblock_task":
    case "resource_available":
    case "add_guidance":
      return t(`assistant.actionTitle.${action.type}`, { task: subject ?? "" }).trim();
    case "create_task":
      return t(action.args.start ? "assistant.actionTitle.create_task_start" : "assistant.actionTitle.create_task", { title: action.args.title.slice(0, 80) });
    case "open_settings_form":
      return t("assistant.actionTitle.open_settings_form", { field: t(`assistant.field.${action.args.field}`) });
    case "decide_approval":
      return t("assistant.actionTitle.decide_approval", { option: action.args.option });
    case "create_project_from_brief":
      return t("assistant.actionTitle.create_project_from_brief", { name: action.args.name.slice(0, 80) });
    case "create_project_request":
      return t("assistant.actionTitle.create_project_request", { title: action.args.title });
    case "validate_project":
    case "start_project":
    case "resume_project":
    case "connect_repository":
    case "pause_project":
    case "drain_project":
    case "update_agent_profile":
    case "activate_integration":
    case "activate_routing_config":
    case "publish_project":
      return t(`assistant.actionTitle.${action.type}`);
    // ---------- D34 ----------
    case "update_project_settings": {
      const fields = Object.keys(action.args)
        .filter((k) => k !== "project_id")
        .map((k) => {
          const key = keyOrNull(`assistant.field.${k}`);
          return key ? t(key) : k;
        });
      return t("assistant.actionTitle.update_project_settings", { fields: fields.join(", ") });
    }
    case "set_execution_policy":
      return t(action.args.enabled ? "assistant.actionTitle.set_execution_policy_on" : "assistant.actionTitle.set_execution_policy_off");
    case "edit_task":
    case "cancel_task":
    case "start_task_run":
      return t(`assistant.actionTitle.${action.type}`, { task: subject ?? "" }).trim();
    case "move_task":
      return t("assistant.actionTitle.move_task", { task: subject ?? "", move: t(`assistant.move.${action.args.action}`) });
    case "run_model_probe":
      return t("assistant.actionTitle.run_model_probe", { cents: `${action.args.max_cost_cents}¢` });
    case "update_model_target":
      return t(`assistant.actionTitle.update_model_target_${action.args.state}`);
    case "update_provider_connection":
      return t(`assistant.actionTitle.update_provider_connection_${action.args.state}`);
    // ---------- D36 ----------
    case "start_planning_run":
    case "run_guardian":
    case "review_learnings":
    case "translate_tasks":
      return t(`assistant.actionTitle.${action.type}`);
    case "delete_project":
      return t("assistant.actionTitle.delete_project", { project: subject ?? "" }).trim();
    case "server_operation":
      return t("assistant.actionTitle.server_operation", { server: subject ?? action.args.server_id });
    case "github_operation":
      return t("assistant.actionTitle.github_operation", { operation: `${action.args.method} ${action.args.operation === "create_repository" ? "repository" : action.args.endpoint || "repository settings"}` });
    case "create_system_task":
      return t(action.args.start ? "assistant.actionTitle.create_system_task_start" : "assistant.actionTitle.create_system_task", { title: action.args.title.slice(0, 80) });
    case "guardian_incident_action":
      return t(`assistant.actionTitle.guardian_incident_action_${action.args.action}`);
  }
}

/** Secondary text: a rules reason code through i18n; otherwise the model's words, hidden when the reply is tainted. */
function actionReason(t: Translate, action: AssistantAction, tainted: boolean): string | null {
  const code = keyOrNull(`assistant.actionReason.${action.reason}`);
  if (code) return t(code);
  return tainted ? null : `${action.label} — ${action.reason}`;
}

/** Plain text of a reply for the screen-reader announcement (the same rules as the visible body). */
export function replySpeech(t: Translate, reply: AssistantReply, content: string): string {
  if (reply.mode === "ai" && reply.text) return reply.text;
  const lines = reply.lines.map((l) => lineText(t, l));
  if (reply.lines.length > 0 && lines.every((l) => l !== null)) return lines.join(" ");
  return reply.text || content || t("assistant.nothingToReport");
}

function ResultLine({ tone, children }: { tone: "ok" | "fail" | "busy"; children: ReactNode }) {
  return (
    <p className={`assistant-result assistant-result--${tone}`} data-testid="assistant-action-result">
      {tone === "ok" ? <CheckCircleIcon size={16} /> : tone === "fail" ? <XCircleIcon size={16} /> : <LoaderIcon size={16} className="assistant-spin" />}
      <span className="user-content">{children}</span>
    </p>
  );
}

function ReplyBody({ reply, content }: { reply: AssistantReply; content: string }) {
  const { t } = useI18n();
  const lines = reply.lines.map((l) => lineText(t, l));
  const allKnown = lines.every((l) => l !== null);
  const list = (items: (string | null)[]) => (
    <ul className="assistant-lines">
      {items.map((l, i) => (l ? <li key={i}>{l}</li> : null))}
    </ul>
  );
  if (reply.mode === "ai" && reply.text) {
    return (
      <>
        <Markdown text={reply.text} className="user-content assistant-text" />
        {lines.some((l) => l !== null) ? list(lines) : null}
      </>
    );
  }
  if (reply.lines.length > 0 && allKnown) return list(lines);
  const fallback = reply.text || content;
  return fallback ? <Markdown text={fallback} className="user-content assistant-text" /> : <p className="user-content assistant-text">{t("assistant.nothingToReport")}</p>;
}

export function AssistantMessageView({
  message,
  sessionProjectId,
  outcomes,
  isLast,
  busy,
  onRun,
  onDismiss,
  onSuggestion,
  answeredAttachments,
  answeredTurn,
}: {
  message: AssistantMessage;
  /** The conversation's project (null on Home): a page of another project is titled as such. */
  sessionProjectId: string | null;
  outcomes: Record<string, LocalOutcome>;
  isLast: boolean;
  /** A question is being sent or answered: chips wait. */
  busy: boolean;
  onRun: (index: number) => void;
  onDismiss: (index: number) => void;
  onSuggestion: (text: string) => void;
  /** D36: the attachments of the question this reply answers. */
  answeredAttachments?: readonly AssistantAttachmentRef[] | undefined;
  /** The persisted user turn matched by reply.turn_id, supplied by the conversation owner. */
  answeredTurn?: AssistantTurnMeta | undefined;
}) {
  const { t, locale } = useI18n();
  const reply = message.reply;

  // D34: an automatic follow-up turn is not the user's words — a small note, never a question bubble.
  const followUp = message.turn?.context.follow_up;
  if (message.author_role === "user" && followUp) {
    return (
      <li className="assistant-followup" data-testid="assistant-followup">
        <p className="assistant-note">{t("assistant.followUp.note", { n: followUp.round, max: ASSISTANT_MAX_FOLLOW_UPS })}</p>
      </li>
    );
  }

  if (message.author_role === "user" || !reply) {
    const mine = message.author_role === "user";
    return (
      <li className={`chat-msg chat-msg--${mine ? "user" : "assistant"}`} data-testid="assistant-msg">
        <div className="chat-bubble">
          <p className="user-content assistant-text">{message.content}</p>
          <FileChips files={message.turn?.attachments} testId="assistant-msg-attachments" />
          <MessageTiming message={message} answeredTurn={answeredTurn} />
        </div>
      </li>
    );
  }

  const budget = reply.budget;
  const budgetHigh = budget !== null && budget.day_spent_cents !== null && budget.day_limit_cents > 0 && budget.day_spent_cents >= 0.8 * budget.day_limit_cents;
  const modeNote = reply.mode === "rules" || reply.mode === "not_configured" || reply.mode === "budget_exhausted" ? t(`assistant.mode.${reply.mode}`) : null;
  const cost = reply.mode === "ai" ? [reply.model, reply.cost_cents !== null ? formatCents(reply.cost_cents, locale) : null].filter(Boolean).join(" · ") : "";

  return (
    <li className="chat-msg chat-msg--assistant" data-testid="assistant-msg">
      <div className="chat-bubble chat-bubble--wide assistant-bubble">
        <ReplyBody reply={reply} content={message.content} />
        {reply.artifacts?.length ? (
          <ul className="assistant-artifacts" aria-label={t("assistant.artifacts")} data-testid="assistant-generated-artifacts">
            {reply.artifacts.map(file => <ConversationArtifact key={file.id} file={file} />)}
          </ul>
        ) : null}
        <FileChips files={answeredAttachments} label={t("composer.readAttachments")} testId="assistant-reply-attachments" />

        {reply.actions.map((action, index) => {
          const local = outcomes[`${message.id}:${index}`];
          const claim = reply.claims[String(index)];
          const label = actionTitle(t, action, sessionProjectId);
          if (local?.state === "running") return <ResultLine key={index} tone="busy">{t("assistant.action.running", { label })}</ResultLine>;
          if (local?.state === "elsewhere") return <ResultLine key={index} tone="ok">{t("assistant.action.elsewhere", { label })}</ResultLine>;
          if (local?.state === "dismissed") return <p key={index} className="assistant-note">{t("assistant.action.dismissed", { label })}</p>;
          const done = local?.state === "done" ? local.result : claim && claim.outcome !== "pending" ? { outcome: claim.outcome, detail: claim.detail } : null;
          // D41-B2: the server lost the effect's outcome. A neutral note, never a failure; the server does not re-run it.
          if (done?.outcome === "unknown") return <p key={index} className="assistant-note" data-testid="assistant-action-unknown">{t("assistant.action.unknown", { label })}</p>;
          if (done) {
            return (
              <ResultLine key={index} tone={done.outcome === "succeeded" ? "ok" : "fail"}>
                {t(done.outcome === "succeeded" ? (["review_learnings", "server_operation", "publish_project", "create_project_request"].includes(action.type) ? "assistant.action.requested" : "assistant.action.succeeded") : "assistant.action.failed", { label })}
                {detailText(t, done.detail)}
              </ResultLine>
            );
          }
          if (claim) return <ResultLine key={index} tone="ok">{t("assistant.action.pending", { label })}</ResultLine>;
          // D34: an action the user's role cannot run is shown as refused — no button, never attempted.
          if (action.refused === "role") {
            return (
              <p key={index} className="assistant-note" data-testid="assistant-action-refused">
                {t("assistant.action.refused", { label })}
              </p>
            );
          }
          const reason = actionReason(t, action, reply.tainted);
          const generic = (
            <div key={index} className="assistant-action" data-testid="assistant-action">
              {reason ? <p className="user-content assistant-action-reason">{reason}</p> : null}
              {action.type === "add_guidance" ? <p className="user-content assistant-action-reason">{t("assistant.action.guidance", { text: action.args.text.slice(0, 300) })}</p> : null}
              {action.type === "review_learnings" ? <p className="assistant-note">{t("assistant.action.learningReviewActor")}</p> : null}
              {action.type === "server_operation" ? (
                <div data-testid="assistant-server-operation">
                  <p className="assistant-note">{t("assistant.action.serverTarget", { server: action.args.server_id })}</p>
                  {action.args.project_id ? <p className="assistant-note">{t("assistant.action.serverProject", { project: action.args.project_id })}</p> : null}
                  <p className="assistant-note">{t("assistant.action.serverDisruption", { disruption: t(`assistant.disruption.${action.args.disruption}`) })}</p>
                  <pre className="user-content assistant-brief-text">{action.args.command}</pre>
                  <p className="assistant-note">{t("assistant.action.serverQueuedHint")}</p>
                </div>
              ) : null}
              {action.type === "delete_project" ? <DeleteProjectFacts projectId={action.args.project_id} /> : null}
              {action.type === "github_operation" ? (
                <details className="assistant-brief" data-testid="assistant-github-operation">
                  <summary>{t("assistant.action.githubRequest")}</summary>
                  <pre className="user-content assistant-brief-text">{JSON.stringify(action.args, null, 2)}</pre>
                </details>
              ) : null}
              {action.type === "create_project_from_brief" ? (
                // The brief is what the click sends: always reviewable before confirming, even on a tainted reply.
                <details className="assistant-brief" data-testid="assistant-brief">
                  <summary>{t("assistant.action.reviewBrief", { count: action.args.brief.length })}</summary>
                  <pre className="user-content assistant-brief-text">{action.args.brief}</pre>
                </details>
              ) : null}
              <div className="assistant-action-buttons">
                <Button size="sm" variant="primary" onClick={() => onRun(index)}>
                  <span className="user-content">{label}</span>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onDismiss(index)}>
                  {t("assistant.action.notNow")}
                </Button>
              </div>
            </div>
          );
          // D41-C4: a decision of a pending service_grant is answered by its grant card, never by the generic Run button.
          return action.type === "decide_approval" ? <DecisionAction key={index} approvalId={action.args.approval_id} generic={generic} /> : generic;
        })}

        {isLast && !busy && reply.suggestions.length > 0 ? (
          <ul className="assistant-chips" data-testid="assistant-suggestions" aria-label={t("assistant.suggestions")}>
            {reply.suggestions.map((s, i) => (
              <li key={i}>
                <button type="button" className="assistant-chip" onClick={() => onSuggestion(s)}>
                  {s}
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {modeNote ? (
          <p className="assistant-note" data-testid="assistant-mode-note">
            {modeNote}
          </p>
        ) : null}
        {budgetHigh ? (
          <p className="assistant-note assistant-note--warning" data-testid="assistant-budget-note">
            {t("assistant.budgetHigh")}
          </p>
        ) : null}
        {cost ? <p className="assistant-note">{cost}</p> : null}
        {reply.diagnostics ? (
          <details className="assistant-brief" data-testid="assistant-diagnostics">
            <summary>{t("assistant.diagnostics.summary")}</summary>
            <p className="assistant-note mono">{t("assistant.diagnostics.profile", { id: reply.diagnostics.profile_id })}</p>
            <p className="assistant-note mono">{t("assistant.diagnostics.version", { id: reply.diagnostics.profile_version_id ?? t("assistant.diagnostics.noVersion") })}</p>
            <p className="assistant-note mono">{t("assistant.diagnostics.capabilities", { count: reply.diagnostics.capabilities.length, list: reply.diagnostics.capabilities.join(", ") || "—" })}</p>
          </details>
        ) : null}
        <MessageTiming message={message} answeredTurn={answeredTurn} />
      </div>
    </li>
  );
}
